import React from 'react';
import {
  Box,
  Container,
  VStack,
  Heading,
  Text,
  Button,
  SimpleGrid,
  Card,
  CardBody,
  HStack,
  Icon,
  useColorModeValue,
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FaPlay,
  FaUsers,
  FaTrophy,
  FaChartLine,
  FaServer,
  FaCog,
  FaCheck,
  FaCalendarAlt,
  FaComments,
  FaMoneyBillWave,
  FaArrowRight,
} from 'react-icons/fa';

const ProductsPage: React.FC = () => {
  const { t } = useTranslation();

  return (
    <Box>
      {/* Hero Section */}
      <Box
        bg='linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
        color='white'
        py={{ base: 12, md: 16, lg: 20 }}
      >
        <Container maxW='7xl'>
          <VStack spacing={{ base: 6, md: 8 }} textAlign='center'>
            <VStack spacing={{ base: 3, md: 4 }}>
              <Heading
                size={{ base: 'xl', md: '2xl', lg: '3xl' }}
                lineHeight='1.2'
              >
                {t('productsPage.hero.title')}
              </Heading>
              <Text
                fontSize={{ base: 'lg', md: 'xl' }}
                opacity='0.9'
                maxW='3xl'
              >
                {t('productsPage.hero.subtitle')}
              </Text>
            </VStack>
          </VStack>
        </Container>
      </Box>

      {/* Products Grid */}
      <Box
        bg={useColorModeValue('gray.50', 'gray.800')}
        py={{ base: 12, md: 16 }}
      >
        <Container maxW='7xl'>
          <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={{ base: 6, md: 8 }}>
            {/* Poker Product */}
            <Card
              variant='outline'
              bg='linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)'
              borderColor='primary.300'
              _hover={{
                transform: 'translateY(-4px)',
                boxShadow: '2xl',
                borderColor: 'primary.400',
              }}
              transition='all 0.3s'
              h='full'
            >
              <CardBody p={8} display='flex' flexDirection='column' h='full'>
                <VStack spacing={6} align='stretch' flex='1'>
                  <HStack spacing={4}>
                    <Box
                      p={3}
                      bg='primary.100'
                      borderRadius='xl'
                      color='primary.600'
                    >
                      <Icon as={FaPlay} boxSize={8} />
                    </Box>
                    <Heading
                      size='xl'
                      color={useColorModeValue('gray.800', 'white')}
                    >
                      {t('productsPage.poker.title')}
                    </Heading>
                  </HStack>

                  <Text
                    fontSize='lg'
                    color={useColorModeValue('gray.600', 'gray.300')}
                    lineHeight='tall'
                    minH='120px'
                  >
                    {t('productsPage.poker.description')}
                  </Text>

                  <VStack spacing={3} align='stretch' flex='1'>
                    <HStack spacing={3}>
                      <Icon as={FaCheck} color='primary.500' />
                      <Text color={useColorModeValue('gray.700', 'gray.300')}>
                        {t('productsPage.poker.features.tournaments')}
                      </Text>
                    </HStack>
                    <HStack spacing={3}>
                      <Icon as={FaCheck} color='primary.500' />
                      <Text color={useColorModeValue('gray.700', 'gray.300')}>
                        {t('productsPage.poker.features.analytics')}
                      </Text>
                    </HStack>
                    <HStack spacing={3}>
                      <Icon as={FaCheck} color='primary.500' />
                      <Text color={useColorModeValue('gray.700', 'gray.300')}>
                        {t('productsPage.poker.features.multiplayer')}
                      </Text>
                    </HStack>
                    <HStack spacing={3}>
                      <Icon as={FaCheck} color='primary.500' />
                      <Text color={useColorModeValue('gray.700', 'gray.300')}>
                        {t('productsPage.poker.features.customRules')}
                      </Text>
                    </HStack>
                  </VStack>

                  <Button
                    as={RouterLink}
                    to='/products/poker'
                    size='lg'
                    colorScheme='primary'
                    rightIcon={<FaArrowRight />}
                    w='full'
                    mt='auto'
                    _hover={{
                      transform: 'translateY(-2px)',
                      boxShadow: 'lg',
                    }}
                  >
                    {t('productsPage.poker.button')}
                  </Button>
                </VStack>
              </CardBody>
            </Card>

            {/* ClubApp Product */}
            <Card
              variant='outline'
              bg='linear-gradient(135deg, rgba(184, 134, 11, 0.1) 0%, rgba(146, 64, 14, 0.1) 100%)'
              borderColor='accent.300'
              _hover={{
                transform: 'translateY(-4px)',
                boxShadow: '2xl',
                borderColor: 'accent.400',
              }}
              transition='all 0.3s'
              h='full'
            >
              <CardBody p={8} display='flex' flexDirection='column' h='full'>
                <VStack spacing={6} align='stretch' flex='1'>
                  <HStack spacing={4}>
                    <Box
                      p={3}
                      bg='accent.100'
                      borderRadius='xl'
                      color='accent.600'
                    >
                      <Icon as={FaUsers} boxSize={8} />
                    </Box>
                    <Heading
                      size='xl'
                      color={useColorModeValue('gray.800', 'white')}
                    >
                      {t('productsPage.clubApp.title')}
                    </Heading>
                  </HStack>

                  <Text
                    fontSize='lg'
                    color={useColorModeValue('gray.600', 'gray.300')}
                    lineHeight='tall'
                    minH='120px'
                  >
                    {t('productsPage.clubApp.description')}
                  </Text>

                  <VStack spacing={3} align='stretch' flex='1'>
                    <HStack spacing={3}>
                      <Icon as={FaCheck} color='accent.500' />
                      <Text color={useColorModeValue('gray.700', 'gray.300')}>
                        {t('productsPage.clubApp.features.memberManagement')}
                      </Text>
                    </HStack>
                    <HStack spacing={3}>
                      <Icon as={FaCheck} color='accent.500' />
                      <Text color={useColorModeValue('gray.700', 'gray.300')}>
                        {t('productsPage.clubApp.features.eventScheduling')}
                      </Text>
                    </HStack>
                    <HStack spacing={3}>
                      <Icon as={FaCheck} color='accent.500' />
                      <Text color={useColorModeValue('gray.700', 'gray.300')}>
                        {t('productsPage.clubApp.features.socialFeatures')}
                      </Text>
                    </HStack>
                    <HStack spacing={3}>
                      <Icon as={FaCheck} color='accent.500' />
                      <Text color={useColorModeValue('gray.700', 'gray.300')}>
                        {t('productsPage.clubApp.features.revenueTracking')}
                      </Text>
                    </HStack>
                  </VStack>

                  <Button
                    as={RouterLink}
                    to='/products/club'
                    size='lg'
                    bg='orange.500'
                    color='white'
                    rightIcon={<FaArrowRight />}
                    w='full'
                    mt='auto'
                    _hover={{
                      bg: 'orange.600',
                      transform: 'translateY(-2px)',
                      boxShadow: 'lg',
                    }}
                  >
                    {t('productsPage.clubApp.button')}
                  </Button>
                </VStack>
              </CardBody>
            </Card>
          </SimpleGrid>
        </Container>
      </Box>

      {/* Feature Comparison */}
      <Box
        bg={useColorModeValue('gray.50', 'gray.800')}
        py={{ base: 12, md: 16 }}
      >
        <Container maxW='7xl'>
          <VStack spacing={12}>
            <VStack spacing={4} textAlign='center'>
              <Heading size='2xl' color='primary.500'>
                {t('productsPage.comparison.title')}
              </Heading>
              <Text
                fontSize='lg'
                color={useColorModeValue('gray.600', 'gray.300')}
                maxW='2xl'
              >
                {t('productsPage.comparison.subtitle')}
              </Text>
            </VStack>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8} w='full'>
              <VStack
                spacing={6}
                p={8}
                bg={useColorModeValue('white', 'gray.700')}
                borderRadius='xl'
                boxShadow='md'
                h='full'
                justify='space-between'
              >
                <VStack spacing={6}>
                  <Icon as={FaServer} boxSize={12} color='primary.500' />
                  <Heading
                    size='lg'
                    textAlign='center'
                    color={useColorModeValue('gray.800', 'white')}
                  >
                    {t('productsPage.comparison.pokerFeatures.title')}
                  </Heading>
                </VStack>
                <VStack spacing={3} align='start' w='full'>
                  <HStack spacing={3}>
                    <Icon as={FaTrophy} color='primary.500' />
                    <Text color={useColorModeValue('gray.700', 'gray.300')}>
                      {t(
                        'productsPage.comparison.pokerFeatures.tournamentManagement'
                      )}
                    </Text>
                  </HStack>
                  <HStack spacing={3}>
                    <Icon as={FaChartLine} color='primary.500' />
                    <Text color={useColorModeValue('gray.700', 'gray.300')}>
                      {t(
                        'productsPage.comparison.pokerFeatures.realTimeAnalytics'
                      )}
                    </Text>
                  </HStack>
                  <HStack spacing={3}>
                    <Icon as={FaPlay} color='primary.500' />
                    <Text color={useColorModeValue('gray.700', 'gray.300')}>
                      {t(
                        'productsPage.comparison.pokerFeatures.multiTableSupport'
                      )}
                    </Text>
                  </HStack>
                  <HStack spacing={3}>
                    <Icon as={FaCog} color='primary.500' />
                    <Text color={useColorModeValue('gray.700', 'gray.300')}>
                      {t(
                        'productsPage.comparison.pokerFeatures.customGameRules'
                      )}
                    </Text>
                  </HStack>
                </VStack>
              </VStack>

              <VStack
                spacing={6}
                p={8}
                bg={useColorModeValue('white', 'gray.700')}
                borderRadius='xl'
                boxShadow='md'
                h='full'
                justify='space-between'
              >
                <VStack spacing={6}>
                  <Icon as={FaUsers} boxSize={12} color='accent.500' />
                  <Heading
                    size='lg'
                    textAlign='center'
                    color={useColorModeValue('gray.800', 'white')}
                  >
                    {t('productsPage.comparison.clubAppFeatures.title')}
                  </Heading>
                </VStack>
                <VStack spacing={3} align='start' w='full'>
                  <HStack spacing={3}>
                    <Icon as={FaUsers} color='accent.500' />
                    <Text color={useColorModeValue('gray.700', 'gray.300')}>
                      {t(
                        'productsPage.comparison.clubAppFeatures.memberManagement'
                      )}
                    </Text>
                  </HStack>
                  <HStack spacing={3}>
                    <Icon as={FaCalendarAlt} color='accent.500' />
                    <Text color={useColorModeValue('gray.700', 'gray.300')}>
                      {t(
                        'productsPage.comparison.clubAppFeatures.eventScheduling'
                      )}
                    </Text>
                  </HStack>
                  <HStack spacing={3}>
                    <Icon as={FaComments} color='accent.500' />
                    <Text color={useColorModeValue('gray.700', 'gray.300')}>
                      {t(
                        'productsPage.comparison.clubAppFeatures.socialFeatures'
                      )}
                    </Text>
                  </HStack>
                  <HStack spacing={3}>
                    <Icon as={FaMoneyBillWave} color='accent.500' />
                    <Text color={useColorModeValue('gray.700', 'gray.300')}>
                      {t(
                        'productsPage.comparison.clubAppFeatures.revenueTracking'
                      )}
                    </Text>
                  </HStack>
                </VStack>
              </VStack>
            </SimpleGrid>
          </VStack>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box bg='primary.500' color='white' py={16}>
        <Container maxW='4xl'>
          <VStack spacing={8} textAlign='center'>
            <Heading size='2xl'>{t('productsPage.cta.title')}</Heading>
            <Text fontSize='xl' maxW='2xl'>
              {t('productsPage.cta.subtitle')}
            </Text>
            <VStack spacing={4} w='full'>
              <HStack
                spacing={{ base: 4, md: 6 }}
                flexWrap='wrap'
                justify='center'
              >
                <Button
                  as={RouterLink}
                  to='/contact'
                  size={{ base: 'md', md: 'lg' }}
                  variant='solid'
                  bg='white'
                  color='primary.500'
                  _hover={{ bg: 'gray.100', transform: 'translateY(-2px)' }}
                  rightIcon={<FaArrowRight />}
                  w={{ base: 'full', sm: 'auto' }}
                  minW={{ base: 'auto', sm: '160px' }}
                >
                  {t('productsPage.cta.contactButton')}
                </Button>
                <Button
                  as={RouterLink}
                  to='/demo'
                  size={{ base: 'md', md: 'lg' }}
                  variant='outline'
                  borderColor='white'
                  color='white'
                  _hover={{
                    bg: 'whiteAlpha.200',
                    transform: 'translateY(-2px)',
                  }}
                  w={{ base: 'full', sm: 'auto' }}
                  minW={{ base: 'auto', sm: '160px' }}
                >
                  {t('productsPage.cta.demoButton')}
                </Button>
              </HStack>
            </VStack>
          </VStack>
        </Container>
      </Box>
    </Box>
  );
};

export default ProductsPage;
